import { debugLog, errorLog } from './logger';
import type { FeeEnvironment } from '../types';

/**
 * Response from the fee API
 */
interface FeeApiResponse {
  hyperliquid: {
    tier: number;
    fee_bps: number;
    total_30d_volume: number;
    next_tier_volume_required: number;
    volume_breakdown: {
      manual_volume: number;
      auto_volume: number;
    };
  };
}

/**
 * Environment to backend API URL mapping
 */
const ENVIRONMENT_URLS: Record<FeeEnvironment, string> = {
  local: 'http://localhost:3010',
  staging: 'https://nomy-backend-staging.biconomy.io',
  prod: 'https://nomy-backend.biconomy.io',
};

/**
 * Service to fetch and cache dynamic builder fees from backend
 */
export class FeeService {
  private currentFeeBps: number;
  private walletAddress: string;
  private apiBaseUrl: string;
  private refreshIntervalId?: ReturnType<typeof setInterval>;
  private readonly DEFAULT_FEE_BPS = 1; // Default to 1 bps if API fails
  private readonly REFRESH_INTERVAL_MS = 60000; // 1 minute

  constructor(walletAddress: string, environment: FeeEnvironment = 'prod') {
    this.walletAddress = walletAddress;
    this.apiBaseUrl = ENVIRONMENT_URLS[environment];
    this.currentFeeBps = this.DEFAULT_FEE_BPS; // Start with default
    debugLog(`[Fee Service] Using ${environment} environment: ${this.apiBaseUrl}`);
  }

  /**
   * Get current fee in bps
   * Returns cached value, updated by background refresh
   */
  getFeeBps(): number {
    return this.currentFeeBps;
  }

  /**
   * Get current fee in Hyperliquid format (tenths of a basis point)
   * 1 bps = 10 tenths of bps
   * Must return integer as Hyperliquid API requires it (e.g., 1.25 bps -> 13)
   */
  getFeeInTenthsOfBps(): number {
    return Math.round(this.currentFeeBps * 10);
  }

  /**
   * Fetch fee from API
   * @private
   */
  private async fetchFee(): Promise<void> {
    try {
      const url = `${this.apiBaseUrl}/v1/user/fee?wallet_address=${this.walletAddress}`;

      debugLog('[Fee Service] Fetching dynamic fee from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 400) {
          errorLog(`[Fee Service] Invalid wallet address: ${this.walletAddress}`);
        } else {
          errorLog(`[Fee Service] API returned status ${response.status}`);
        }
        // Keep current fee on error
        return;
      }

      const data = await response.json() as FeeApiResponse;

      if (data.hyperliquid && typeof data.hyperliquid.fee_bps === 'number') {
        const newFee = data.hyperliquid.fee_bps;

        if (newFee !== this.currentFeeBps) {
          debugLog(`[Fee Service] Fee updated: ${this.currentFeeBps} bps → ${newFee} bps (tier ${data.hyperliquid.tier})`);
          this.currentFeeBps = newFee;
        } else {
          debugLog(`[Fee Service] Fee unchanged: ${this.currentFeeBps} bps (tier ${data.hyperliquid.tier})`);
        }
      } else {
        errorLog('[Fee Service] Invalid response format:', data);
      }
    } catch (error) {
      errorLog('[Fee Service] Failed to fetch fee:', error);
      // Keep current fee on error
    }
  }

  /**
   * Start background fee refresh
   * Fetches immediately, then every 1 minute
   */
  async start(): Promise<void> {
    debugLog('[Fee Service] Starting background fee refresh');

    // Fetch immediately
    await this.fetchFee();

    // Then fetch every 1 minute
    this.refreshIntervalId = setInterval(() => {
      this.fetchFee().catch(error => {
        errorLog('[Fee Service] Background fetch error:', error);
      });
    }, this.REFRESH_INTERVAL_MS);

    debugLog(`[Fee Service] Background refresh scheduled (every ${this.REFRESH_INTERVAL_MS / 1000}s)`);
  }

  /**
   * Stop background fee refresh
   * Called when client is destroyed
   */
  stop(): void {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = undefined;
      debugLog('[Fee Service] Background refresh stopped');
    }
  }

  /**
   * Manually trigger fee refresh
   * Useful for testing or forcing immediate update
   */
  async refresh(): Promise<void> {
    await this.fetchFee();
  }
}
